// Ventana de filas visibles de la hoja (renderizado por ventana). Lógica pura.
//
// La hoja completa son ~4.000 nodos: cada cambio de densidad, de pestaña, de orden o
// de filtro obliga al navegador a recalcular estilo y layout de todos. Pintando solo
// las filas cercanas a la zona visible (más un margen) ese coste baja a una cuarta
// parte. Las filas no pintadas se sustituyen por dos filas espaciadoras con su alto.
//
// Las alturas reales se miden al pintar cada fila (varían: textos que saltan de
// línea); las que aún no se han medido usan la media de las medidas o una estimación.

// heights: array con el alto medido de cada fila (undefined si no se conoce).
export function averageHeight(heights, fallback) {
    let sum = 0;
    let count = 0;
    heights.forEach((height) => {
        if (height > 0) { sum += height; count += 1; }
    });
    return count ? sum / count : fallback;
}

// Filas [start, end) a pintar para la zona visible [scrollTop, scrollTop + viewportHeight]
// ampliada `overscan` px por arriba y por abajo, y alto de los espaciadores.
export function computeRowWindow({ heights, scrollTop = 0, viewportHeight = 0, overscan = 0, fallbackHeight = 40 }) {
    const count = heights.length;
    const estimate = averageHeight(heights, fallbackHeight);
    const from = Math.max(0, scrollTop - overscan);
    const to = scrollTop + viewportHeight + overscan;
    let y = 0;
    let start = -1;
    let end = count;
    let topPad = 0;
    let beforeEnd = 0;
    for (let index = 0; index < count; index += 1) {
        const height = heights[index] > 0 ? heights[index] : estimate;
        if (start === -1 && y + height > from) { start = index; topPad = y; }
        if (start !== -1 && end === count && y >= to) { end = index; beforeEnd = y; }
        y += height;
    }
    if (start === -1) {
        // Scroll por debajo del final (p. ej. tras filtrar): se pintan las últimas filas.
        start = Math.max(0, count - 1);
        topPad = count ? y - (heights[count - 1] > 0 ? heights[count - 1] : estimate) : 0;
    }
    if (end === count) beforeEnd = y;
    return { start, end, topPad, bottomPad: Math.max(0, y - beforeEnd), totalHeight: y, estimate };
}

// Posición vertical (px desde el inicio del cuerpo) de la fila `index`.
export function rowOffset(heights, index, fallbackHeight = 40) {
    const estimate = averageHeight(heights, fallbackHeight);
    let y = 0;
    for (let i = 0; i < Math.min(index, heights.length); i += 1) y += heights[i] > 0 ? heights[i] : estimate;
    return y;
}

// ¿Se enseña el botón de volver a la primera obra? Solo cuando se ha bajado más de una
// pantalla (con un mínimo, para que en una hoja baja no aparezca por un poco de scroll).
export const BACK_TO_TOP_MIN_PX = 320;
export function showBackToTop({ top = 0, height = 0 } = {}) {
    return top > Math.max(height, BACK_TO_TOP_MIN_PX);
}

// ¿Hay que actualizar la posición guardada? Solo al moverse un tramo (evita un render
// por cada evento de scroll) o si cambia el alto visible.
export function shouldUpdateViewport(previous, next, step) {
    return Math.abs(previous.top - next.top) >= step || previous.height !== next.height;
}
